/**
 * One dispatcher's telephone, as the administrator sets it.
 *
 * Four questions, and whose they are matters. Which telephone rings and the fallback to the
 * other are the dispatcher's too — they can change those from the console. What a second
 * caller does and which of the firm's lines ring for them are the office's alone: they
 * decide who answers what, and a dispatcher who could untick a line could stop answering
 * it.
 *
 * No setting here can drop a call. A dispatcher given only some lines still hears the
 * others when nobody who was given them can answer; the dialog says so, because an
 * administrator who does not know it will worry about exactly that.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { describe } from '../errors';
import { useLineNames } from '../../telephony/lines';
import { settingsApi } from '../api';
import { Banner, Button, Dialog, EmptyState, Field, Measured, Select, Switch } from '../../ui';
import type { AnswerOn, WhenOnACall } from '../../telephony/api';
import { deskPhonesApi, handlingLimits, type DispatcherTelephone } from '../telephony/deskPhones';
import type { StaffMember } from './staff';

export function DispatcherTelephoneDialog({
  member,
  onClose,
}: {
  member: StaffMember | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<DispatcherTelephone | null>(null);
  const [lines, setLines] = useState<string[]>([]);
  const listed = useLineNames();

  /** The label of a listed line whose number this is, or null when none has it. */
  const labelOf = (number: string) => listed.find((l) => l.number === number)?.label ?? null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (member === null) return;

    let cancelled = false;

    void (async () => {
      setSettings(null);
      setError(null);

      try {
        const [theirs, telephony] = await Promise.all([
          deskPhonesApi.dispatcher(member.userId),
          settingsApi.telephony(),
        ]);

        if (cancelled) return;

        setSettings(theirs);
        setLines(telephony.inboundNumbers);
      } catch (failure) {
        if (!cancelled) setError(describe(failure, t, 'settings.couldNotLoad'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [member, t]);

  function change<K extends keyof DispatcherTelephone>(key: K, value: DispatcherTelephone[K]) {
    setSettings((current) => (current === null ? current : { ...current, [key]: value }));
  }

  async function save() {
    if (member === null || settings === null) return;

    setBusy(true);
    setError(null);

    try {
      await deskPhonesApi.saveDispatcher(member.userId, {
        answerOn: settings.answerOn,
        ringOtherWhenUnavailable: settings.ringOtherWhenUnavailable,
        whenOnACall: settings.whenOnACall,
        allLines: settings.allLines,
        lines: settings.allLines ? [] : settings.lines,
        alsoRingOtherAfterSeconds: settings.alsoRingOtherAfterSeconds,
      });

      onClose();
    } catch (failure) {
      setError(describe(failure, t, 'fleet.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={member !== null}
      wide
      title={t('fleet.telephone.title', { name: member?.displayName ?? member?.userId ?? '' })}
      onClose={onClose}
      actions={
        <>
          <Button variant="quiet" onClick={onClose}>
            {t('fleet.cancel')}
          </Button>
          <Button variant="primary" disabled={busy || settings === null} onClick={() => void save()}>
            {t('settings.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error !== null && <Banner tone="danger">{error}</Banner>}

        {settings === null ? (
          error === null && <EmptyState>{t('app.loading')}</EmptyState>
        ) : (
          <>
            <Field label={t('fleet.telephone.answerOn')} help={t('fleet.telephone.answerOnHelp')}>
              <Select
                value={settings.answerOn}
                onChange={(e) => change('answerOn', e.target.value as AnswerOn)}
              >
                <option value="Browser">{t('softphone.answerOnBrowser')}</option>
                <option value="DeskPhone">{t('softphone.answerOnDesk')}</option>
              </Select>
            </Field>

            <div className="flex items-center justify-between gap-3">
              <span>{t('fleet.telephone.ringOther')}</span>
              <Switch
                checked={settings.ringOtherWhenUnavailable}
                label={t('fleet.telephone.ringOther')}
                onChange={(next) => change('ringOtherWhenUnavailable', next)}
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <span>{t('fleet.telephone.alsoRingOther')}</span>
              <span className="flex items-center gap-2">
                {settings.alsoRingOtherAfterSeconds !== null && (
                  <Measured
                    type="number"
                    inputMode="numeric"
                    aria-label={t('fleet.telephone.alsoRingOther')}
                    unit={t('settings.unitSeconds')}
                    value={settings.alsoRingOtherAfterSeconds}
                    min={handlingLimits.alsoRingOtherAfterSeconds.min}
                    max={handlingLimits.alsoRingOtherAfterSeconds.max}
                    onChange={(e) => change('alsoRingOtherAfterSeconds', e.target.valueAsNumber)}
                  />
                )}
                <Switch
                  checked={settings.alsoRingOtherAfterSeconds !== null}
                  label={t('fleet.telephone.alsoRingOther')}
                  onChange={(next) => change('alsoRingOtherAfterSeconds', next ? 10 : null)}
                />
              </span>
            </div>

            <Field
              label={t('fleet.telephone.whenOnACall')}
              help={t('fleet.telephone.whenOnACallHelp')}
            >
              <Select
                value={settings.whenOnACall}
                onChange={(e) => change('whenOnACall', e.target.value as WhenOnACall)}
              >
                <option value="ShowAndTake">{t('fleet.telephone.showAndTake')}</option>
                <option value="SendToOthers">{t('fleet.telephone.sendToOthers')}</option>
              </Select>
            </Field>

            <Field label={t('fleet.telephone.lines')} help={t('fleet.telephone.linesHelp')}>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3">
                  <span>{t('fleet.telephone.allLines')}</span>
                  <Switch
                    checked={settings.allLines}
                    label={t('fleet.telephone.allLines')}
                    onChange={(next) => change('allLines', next)}
                  />
                </div>

                {!settings.allLines &&
                  (lines.length === 0 ? (
                    <p className="m-0 text-secondary text-ink-3">{t('fleet.telephone.noLines')}</p>
                  ) : (
                    lines.map((line) => (
                      <div key={line} className="flex items-center justify-between gap-3 pl-3">
                        <span className="flex items-baseline gap-2 min-w-0">
                          <span className="tabular-nums">{line}</span>
                          {/* The office's name for the line that shows this number, when
                              the administrator has listed one. */}
                          {labelOf(line) !== null && (
                            <span className="text-secondary text-ink-3 truncate">{labelOf(line)}</span>
                          )}
                        </span>
                        <Switch
                          checked={settings.lines.includes(line)}
                          label={line}
                          onChange={(next) =>
                            change(
                              'lines',
                              next
                                ? [...settings.lines, line]
                                : settings.lines.filter((held) => held !== line),
                            )
                          }
                        />
                      </div>
                    ))
                  ))}
              </div>
            </Field>
          </>
        )}
      </div>
    </Dialog>
  );
}
